<template>
  <TagInput
    v-if="allowInlineTagEdits"
    :model-value="tags"
    :user-id="userId"
    :all-tags="store.allTags"
    :allow-new="allowNew"
    :compact="true"
    :can-click="canClick"
    :empty-caption="emptyCaption"
    @tag-click="handleTagClick"
    @focus="store.loadAllTags()"
    @update:model-value="(tag) => updated(tag)"
    @create="(tagName) => created(tagName)"
    @advanced="(tag) => tagEditController.show(tag)"
  />
  <TagList
    v-else-if="tags.length > 0"
    :model-value="tags"
    :can-click="canClick"
    :can-remove="false"
    size="small"
    @tag-click="handleTagClick"
  />
  <TagEditDialog
    v-if="allowInlineTagEdits && editingTag"
    :id="editingTag.id"
    :name="editingTag.name"
    :color="editingTag.color"
    :count="editingTag.count"
    @save="(tag) => tagEditController.save(tag)"
    @cancel="(tag) => tagEditController.cancel(tag)"
    @destroy="(tag) => tagEditController.destroy(tag)"
  />
</template>

<script setup>import { ref as _ref } from 'vue';

  import TagInput from "./tag-input.vue";
  import TagList from "./tag-list.vue";
  import TagEditDialog from "./tag-edit-dialog.vue";

  import { useRecipeTagStore } from "./stores/recipe-tag-store.js";
  import { useTagEditController } from "./lib/tag-edit-controller.js";

  const props = defineProps({
    userId: { type: String, required: true },
    recipeId: { type: String, required: true },
    tags: { type: Array, required: false, default: () => [] },
    emptyCaption: { type: String, default: undefined },
    canClick: { type: Boolean, default: false },
    allowNew: { type: Boolean, default: true },
    allowInlineTagEdits: { type: Boolean, default: true },
  });

  const emit = defineEmits(["tag-click"]);

  const store = useRecipeTagStore();
  store.setUserId(props.userId);

  let tags = _ref(props.tags);

  /* The tag that is being edited, or null */
  let editingTag = _ref(null);

  const tagEditController = useTagEditController({
    store,
    editingTag: (editingTag),
    tags: (tags),
  });

  const updated = (newTags) => {
    /* We have the new list.  Find items that have been added or removed */
    const added = newTags.filter((tag) => !tags.value.find((t) => t.name === tag.name));
    const removed = tags.value.filter((tag) => !newTags.find((t) => t.id === tag.id));

    /* Update the UI */
    tags.value = newTags;

    /* For new tags, we need to actually create them on the backend */
    added.forEach(async (tag) => {
      const newTag = await store.createTag({ modelId: props.recipeId, tag });
      if (!newTag) return;

      /* Update our local ID to match the RecipeTag id */
      const entry = tags.value.find((t) => t.name === tag.name);
      entry.id = newTag.id.toString();
    });

    /* Destroy any removed tags */
    removed.forEach((tag) => store.destroyTag({ tag }));
  };

  const created = async (name) => {
    /* Create the tag to get a color and ID */
    const newTag = await store.createTag({
      modelId: props.recipeId,
      tag: { name },
    });
    if (!newTag) return;

    tags.value = [...tags.value, newTag];
  };

  function handleTagClick(tag) {
    console.warn(`[recipe-tag-input][handleTagClick] tag=${tag}`);
    return emit("tag-click", tag);
  }
</script>
